// Helper utilities for keeping the FCM device token in sync with the server.
// Used by NotificationProvider to detect token refreshes every 6 hours and
// re-register the new token without requiring the user to re-subscribe.

import { deleteToken, getToken, Messaging } from "firebase/messaging";
import { getClientDeviceType, getClientTimeZone } from "@/app/lib/utils";

// localStorage key that remembers the last FCM token we successfully
// registered with the server from this browser/device.
export const LAST_KNOWN_TOKEN_KEY = "fcm_last_known_token";

// Interval at which the client checks whether FCM has rotated the token.
// 6 hours matches the Firebase guidance for opportunistic refresh checks.
export const TOKEN_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Fetches the current FCM registration token for this device.
 * Returns null on any failure so callers can skip silently.
 */
export async function getCurrentFcmToken(
  messaging: Messaging,
  registration: ServiceWorkerRegistration
): Promise<string | null> {
  try {
    // getToken returns the same token if it has not rotated, or a new one
    // if FCM regenerated it (reinstall, OS update, security refresh, etc.).
    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  }
  catch (error) {
    // Best-effort: refresh checks must never crash the app.
    console.warn("[fcmTokenSync] Failed to get current FCM token:", error);
    return null;
  }
}

/**
 * Registers a token with the server, recovering from a dead-token rejection.
 *
 * The server validates every new token against FCM (dry-run) and answers
 * 410 TOKEN_DEAD when it is dead. That means the token this client holds is
 * poisoned — Firebase's getToken() would keep returning it from IndexedDB
 * cache forever. So on a 410 we call deleteToken() (wipes the cached
 * tokenDetails AND the underlying push subscription), force getToken() to
 * mint a genuinely fresh token, and retry the registration exactly once.
 *
 * Returns the token that was successfully registered (the fresh one if
 * recovery happened), or null on rejection/failure. Callers that cache the
 * registered token (LAST_KNOWN_TOKEN_KEY) must store the returned value.
 */
export async function registerTokenWithRecovery(
  token: string,
  messaging: Messaging,
  registration: ServiceWorkerRegistration,
  deviceType: string,
  timeZone?: string | null
): Promise<string | null> {
  const post = (t: string) =>
    fetch("/api/push-notification/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Send token, deviceType, and timeZone together so the backend can
      // route to the correct registration logic (personal vs work device).
      body: JSON.stringify({ token: t, deviceType, timeZone }),
    });

  try {
    let response = await post(token);

    if (response.status === 410) {
      console.warn("[fcmTokenSync] Token rejected as dead; purging cached token and retrying once.");
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(LAST_KNOWN_TOKEN_KEY);
      }
      // deleteToken clears IndexedDB tokenDetails and unsubscribes the push
      // subscription — the two places the dead token anchors itself.
      await deleteToken(messaging);
      const freshToken = await getCurrentFcmToken(messaging, registration);
      if (!freshToken) {
        return null;
      }
      response = await post(freshToken);
      // Still rejected (or any other failure): give up — retrying further
      // would just loop on FCM's rejection.
      return response.ok ? freshToken : null;
    }

    return response.ok ? token : null;
  }
  catch (error) {
    console.warn("[fcmTokenSync] Failed to register token:", error);
    return null;
  }
}

/**
 * Sends the (possibly refreshed) token to the server's register endpoint.
 * The server is already idempotent — it de-dupes by token base part — so
 * calling this with an unchanged token is safe but wasteful, which is why
 * callers should diff against LAST_KNOWN_TOKEN_KEY first.
 * Returns the token that was registered (after dead-token recovery if any),
 * or null on failure.
 */
export async function syncTokenWithServer(
  token: string,
  messaging: Messaging,
  registration: ServiceWorkerRegistration
): Promise<string | null> {
  const deviceType = getClientDeviceType();
  const timeZone = getClientTimeZone();
  return registerTokenWithRecovery(token, messaging, registration, deviceType, timeZone);
}

/**
 * Runs one refresh check: gets the current token, compares it with what we
 * last registered, and re-syncs only if it has changed. Safe to call on
 * every interval tick and on app mount.
 */
export async function checkAndSyncToken(
  messaging: Messaging,
  registration: ServiceWorkerRegistration
): Promise<void> {
  try {
    // Skip when the user has not granted notification permission. There is
    // nothing meaningful to sync in that case.
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
      return;
    }

    const currentToken = await getCurrentFcmToken(messaging, registration);
    if (!currentToken) {
      return;
    }

    // Compare against the last token we know the server has for this device.
    const lastKnownToken = typeof window !== "undefined"
      ? window.localStorage.getItem(LAST_KNOWN_TOKEN_KEY)
      : null;

    // No change — server already has this token, skip the network call.
    if (lastKnownToken === currentToken) {
      return;
    }

    // Token has changed (or this is the first check after subscribing).
    // Re-register and remember it on success. Store the token that was
    // actually registered — after a dead-token recovery that is the freshly
    // minted one, not currentToken.
    const registeredToken = await syncTokenWithServer(currentToken, messaging, registration);
    if (registeredToken && typeof window !== "undefined") {
      window.localStorage.setItem(LAST_KNOWN_TOKEN_KEY, registeredToken);
      console.log("[fcmTokenSync] Token refreshed and re-registered with server");
    }
  }
  catch (error) {
    // Final safety net — never let a refresh check bubble up.
    console.warn("[fcmTokenSync] checkAndSyncToken failed:", error);
  }
}
